import type {
  CompanySettings,
  EditGrant,
  RequestStatus,
  TimesheetStatus,
} from '@klukka/core';
import {
  ApiError,
  type EmployeePatch,
  type EmployeeSnapshot,
  type EmployerSnapshot,
  type HourCorrectionInput,
  type ImportResult,
  type KlukkaApi,
  type NewEmployeeInput,
  type NewRequestInput,
  type OrlofEntryInput,
  type OtpChallenge,
  type PayrollPeriod,
  type PayrollPeriodView,
  type PayslipDelivery,
  type PayslipNotifyResult,
  type Portal,
  type SelfServicePatch,
  type Session,
} from './contract.js';

/**
 * REST adapter for the Azure Functions API.
 *
 * The bearer token is kept in `sessionStorage` rather than a cookie so the two
 * portals, served from different origins, never share a session by accident.
 */

const TOKEN_KEY = 'klukka.token.v1';

export interface HttpApiOptions {
  baseUrl?: string;
  storage?: Storage;
}

export class HttpApi implements KlukkaApi {
  private readonly baseUrl: string;
  private readonly store: Storage;

  constructor(opts: HttpApiOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? '/api').replace(/\/$/, '');
    this.store = opts.storage ?? window.sessionStorage;
  }

  private token(): string | null {
    return this.store.getItem(TOKEN_KEY);
  }

  private async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = this.token();
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: {
          'content-type': 'application/json',
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new ApiError('Network unavailable', 0, 'offline');
    }

    if (res.status === 204) return undefined as T;

    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = payload as { message?: string; code?: string };
      if (res.status === 401) this.store.removeItem(TOKEN_KEY);
      throw new ApiError(err.message ?? res.statusText, res.status, err.code);
    }
    return payload as T;
  }

  private keepSession(session: Session): Session {
    this.store.setItem(TOKEN_KEY, session.token);
    return session;
  }

  // ---- authentication ----

  requestOtp(kennitala: string, portal: Portal): Promise<OtpChallenge> {
    return this.call('POST', '/auth/request-otp', { kennitala, portal });
  }

  async verifyOtp(challengeId: string, code: string): Promise<Session> {
    return this.keepSession(await this.call<Session>('POST', '/auth/verify-otp', { challengeId, code }));
  }

  async completeFirstLogin(challengeId: string): Promise<Session> {
    return this.keepSession(await this.call<Session>('POST', '/auth/first-login', { challengeId }));
  }

  async currentSession(): Promise<Session | null> {
    if (!this.token()) return null;
    try {
      return await this.call<Session>('GET', '/auth/session');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.call('POST', '/auth/logout');
    } finally {
      this.store.removeItem(TOKEN_KEY);
    }
  }

  // ---- employee portal ----

  getEmployeeSnapshot(): Promise<EmployeeSnapshot> {
    return this.call('GET', '/me');
  }

  clockIn(): Promise<EmployeeSnapshot> {
    return this.call('POST', '/me/clock-in');
  }

  clockOut(): Promise<EmployeeSnapshot> {
    return this.call('POST', '/me/clock-out');
  }

  confirmOvertime(): Promise<EmployeeSnapshot> {
    return this.call('POST', '/me/confirm-overtime');
  }

  updateMyProfile(patch: SelfServicePatch): Promise<EmployeeSnapshot> {
    return this.call('PATCH', '/me/profile', patch);
  }

  editMyHours(date: string, hours: number): Promise<EmployeeSnapshot> {
    return this.call('PUT', '/me/hours', { date, hours });
  }

  signTimesheet(input: { periodKey: string; label: string; hours: number; signature: string }): Promise<EmployeeSnapshot> {
    return this.call('POST', '/me/timesheets', input);
  }

  createRequest(input: NewRequestInput): Promise<EmployeeSnapshot> {
    return this.call('POST', '/me/requests', input);
  }

  acknowledgeNotification(id: string): Promise<EmployeeSnapshot> {
    return this.call('POST', `/me/notifications/${encodeURIComponent(id)}/ack`);
  }

  // ---- employer portal ----

  getEmployerSnapshot(): Promise<EmployerSnapshot> {
    return this.call('GET', '/company');
  }

  updateSettings(patch: Partial<CompanySettings>): Promise<EmployerSnapshot> {
    return this.call('PATCH', '/company/settings', patch);
  }

  createEmployee(input: NewEmployeeInput): Promise<EmployerSnapshot> {
    return this.call('POST', '/company/employees', input);
  }

  updateEmployee(empId: string, patch: EmployeePatch): Promise<EmployerSnapshot> {
    return this.call('PATCH', `/company/employees/${encodeURIComponent(empId)}`, patch);
  }

  deactivateEmployee(empId: string): Promise<EmployerSnapshot> {
    return this.call('POST', `/company/employees/${encodeURIComponent(empId)}/deactivate`);
  }

  reactivateEmployee(empId: string): Promise<EmployerSnapshot> {
    return this.call('POST', `/company/employees/${encodeURIComponent(empId)}/reactivate`);
  }

  clockOutEmployee(empId: string, at: string): Promise<EmployerSnapshot> {
    return this.call('POST', `/company/employees/${encodeURIComponent(empId)}/clock-out`, { at });
  }

  deleteEmployee(empId: string): Promise<EmployerSnapshot> {
    return this.call('DELETE', `/company/employees/${encodeURIComponent(empId)}`);
  }

  setEditGrant(empId: string, grant: Omit<EditGrant, 'empId' | 'by' | 'ts'> | null): Promise<EmployerSnapshot> {
    return this.call('PUT', `/company/employees/${encodeURIComponent(empId)}/edit-grant`, grant);
  }

  decideRequest(id: string, status: RequestStatus): Promise<EmployerSnapshot> {
    return this.call('POST', `/company/requests/${encodeURIComponent(id)}/decide`, { status });
  }

  decideTimesheet(id: string, status: TimesheetStatus): Promise<EmployerSnapshot> {
    return this.call('POST', `/company/timesheets/${encodeURIComponent(id)}/decide`, { status });
  }

  importWorklogs(csv: string): Promise<{ result: ImportResult; snapshot: EmployerSnapshot }> {
    return this.call('POST', '/company/worklogs/import', { csv });
  }

  createAdjustment(input: { empId: string; date: string; amount: number; note: string }): Promise<EmployerSnapshot> {
    return this.call('POST', '/company/adjustments', input);
  }

  addOrlofEntry({ empId, ...entry }: OrlofEntryInput): Promise<EmployerSnapshot> {
    return this.call('POST', `/company/employees/${encodeURIComponent(empId)}/orlof`, entry);
  }

  // ---- payroll ----

  payrollPeriod(period: { periodStart: string; periodEnd: string }) {
    return this.call<PayrollPeriodView & { snapshot: EmployerSnapshot }>(
      'POST', '/company/payroll/period', period);
  }

  approvePayrollPeriod(period: { periodStart: string; periodEnd: string }) {
    return this.call<PayrollPeriodView & { snapshot: EmployerSnapshot }>(
      'POST', '/company/payroll/period/approve', period);
  }

  reopenPayrollPeriod(period: { periodStart: string; periodEnd: string }) {
    return this.call<PayrollPeriodView & { snapshot: EmployerSnapshot }>(
      'POST', '/company/payroll/period/reopen', period);
  }

  payrollReportPdf(period: { periodStart: string; periodEnd: string }, lang: 'is' | 'en') {
    return this.pdf(`/company/payroll/report?${range(period)}&lang=${lang}`);
  }

  payslipBatchPdf(period: { periodStart: string; periodEnd: string }) {
    return this.pdf(`/company/payroll/payslips?${range(period)}`);
  }

  payslipPdf(period: { periodStart: string; periodEnd: string }, empId: string) {
    return this.pdf(`/company/payroll/payslip?${range(period)}&empId=${encodeURIComponent(empId)}`);
  }

  notifyPayslips(period: { periodStart: string; periodEnd: string }, empId?: string) {
    return this.call<{
      results: PayslipNotifyResult[];
      period: PayrollPeriod;
      deliveries: PayslipDelivery[];
      snapshot: EmployerSnapshot;
    }>('POST', '/company/payroll/notify', empId ? { ...period, empId } : period);
  }

  myPayslipPdf(periodKey: string): Promise<Uint8Array> {
    return this.pdf(`/me/payslip?period=${encodeURIComponent(periodKey)}`);
  }

  timesheetPdf(period: { periodStart: string; periodEnd: string }, empId: string, lang: 'is' | 'en') {
    return this.pdf(`/company/timesheet?${range(period)}&empId=${encodeURIComponent(empId)}&lang=${lang}`);
  }

  timesheetBatchPdf(period: { periodStart: string; periodEnd: string }, lang: 'is' | 'en') {
    return this.pdf(`/company/timesheets?${range(period)}&lang=${lang}`);
  }

  myTimesheetPdf(period: { periodStart: string; periodEnd: string }) {
    return this.pdf(`/me/timesheet?${range(period)}`);
  }

  async emailAvailable(): Promise<boolean> {
    // Reported by the health endpoint so the console can offer a download
    // instead of a notify button rather than discovering it on failure.
    try {
      const res = await fetch(`${this.baseUrl}/health`);
      const body = (await res.json()) as { emailDelivery?: string };
      return body.emailDelivery === 'acs';
    } catch {
      return false;
    }
  }

  /**
   * A PDF, fetched with the bearer token like everything else.
   *
   * These cannot be plain links: the API is on another origin and needs the
   * Authorization header, so the bytes come back and the caller turns them into
   * a download.
   */
  private async pdf(path: string): Promise<Uint8Array> {
    const token = this.token();
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
    } catch {
      throw new ApiError('Network unavailable', 0, 'offline');
    }
    if (!res.ok) {
      const payload = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
      throw new ApiError(payload.message ?? 'Could not produce the document', res.status, payload.code);
    }
    return new Uint8Array(await res.arrayBuffer());
  }

  deleteAdjustment(id: string): Promise<EmployerSnapshot> {
    return this.call('DELETE', `/company/adjustments/${encodeURIComponent(id)}`);
  }

  correctHours(input: HourCorrectionInput): Promise<{ oldH: number; newH: number; snapshot: EmployerSnapshot }> {
    return this.call('POST', '/company/hour-corrections', input);
  }

  deleteHourCorrection(id: string): Promise<EmployerSnapshot> {
    return this.call('DELETE', `/company/hour-corrections/${encodeURIComponent(id)}`);
  }
}

/** The period query string both PDF routes and the report share. */
function range(period: { periodStart: string; periodEnd: string }): string {
  return `start=${encodeURIComponent(period.periodStart)}&end=${encodeURIComponent(period.periodEnd)}`;
}
